/**
 * Client-Side Production Logger
 * Filters console logs based on environment to prevent sensitive data exposure
 */

enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3,
}

/**
 * Environment-based log level configuration
 */
const getLogLevel = (): LogLevel => {
  const env = process.env.NODE_ENV;
  const customLevel = process.env.REACT_APP_LOG_LEVEL;
  
  // If custom level is set, use it
  if (customLevel && LogLevel[customLevel as keyof typeof LogLevel] !== undefined) {
    return LogLevel[customLevel as keyof typeof LogLevel];
  }
  
  // Environment-based defaults
  switch (env) {
    case 'production':
      return LogLevel.ERROR; // 🛡️ MAXIMUM SECURITY: Only errors in production
    case 'staging':
      return LogLevel.WARN; // Errors and warnings in staging
    case 'development':
    default:
      return LogLevel.DEBUG; // Everything in development
  }
};

const currentLogLevel = getLogLevel();

/**
 * Sensitive data patterns to filter from client logs
 */
const SENSITIVE_PATTERNS = [
  /password/i,
  /token/i,
  /secret/i,
  /key/i,
  /auth/i,
  /bearer/i,
  /jwt/i,
  /captcha/i,
  /verification/i,
  /email/i,
  /userid/i,
  /sessionid/i
];

/**
 * Filter sensitive data from objects (client-side)
 */
const filterSensitiveData = (obj: any): any => {
  if (process.env.NODE_ENV === 'production') {
    // In production, be very aggressive about filtering
    if (typeof obj === 'string') {
      return obj.length > 50 ? '[FILTERED]' : obj;
    }
    
    if (Array.isArray(obj)) {
      return `[ARRAY_${obj.length}_ITEMS]`;
    }
    
    if (obj && typeof obj === 'object') {
      const filtered: any = {};
      for (const [key, value] of Object.entries(obj)) {
        if (SENSITIVE_PATTERNS.some(pattern => pattern.test(key))) {
          filtered[key] = '[FILTERED]';
        } else if (typeof value === 'string' && value.length > 100) {
          filtered[key] = '[LONG_STRING]';
        } else if (typeof value === 'object') {
          filtered[key] = '[OBJECT]';
        } else {
          filtered[key] = value;
        }
      }
      return filtered;
    }
  }
  
  // In development/staging, show more but still filter passwords
  if (obj && typeof obj === 'object') {
    const filtered: any = Array.isArray(obj) ? [] : {};
    for (const [key, value] of Object.entries(obj)) {
      if (key.toLowerCase().includes('password')) {
        filtered[key] = '[PASSWORD_FILTERED]';
      } else if (key.toLowerCase().includes('token') && typeof value === 'string') {
        filtered[key] = `${value.substring(0, 8)}...`;
      } else if (typeof value === 'object' && value !== null) {
        filtered[key] = filterSensitiveData(value);
      } else {
        filtered[key] = value;
      }
    }
    return filtered;
  }
  
  return obj;
};

/**
 * Format the log message with timestamp (client-side)
 */
const formatLog = (level: string, message: string, meta?: any): string => {
  const timestamp = new Date().toISOString();
  
  // In production, minimize metadata
  if (process.env.NODE_ENV === 'production') {
    return `[${timestamp}] [CLIENT] [${level}] ${message}`;
  }
  
  // In development/staging, include filtered metadata
  const filteredMeta = meta ? filterSensitiveData(meta) : undefined;
  const metaString = filteredMeta ? ` ${JSON.stringify(filteredMeta)}` : '';
  return `[${timestamp}] [CLIENT] [${level}] ${message}${metaString}`;
};

/**
 * Client-side logger with production filtering
 */
const logger = {
  error: (message: string, meta?: any) => {
    if (currentLogLevel >= LogLevel.ERROR) {
      console.error(formatLog('ERROR', message, meta));
    }
  },
  
  warn: (message: string, meta?: any) => {
    if (currentLogLevel >= LogLevel.WARN) {
      console.warn(formatLog('WARN', message, meta));
    }
  },
  
  info: (message: string, meta?: any) => {
    if (currentLogLevel >= LogLevel.INFO) {
      console.info(formatLog('INFO', message, meta));
    }
  },
  
  debug: (message: string, meta?: any) => {
    if (currentLogLevel >= LogLevel.DEBUG) {
      console.debug(formatLog('DEBUG', message, meta));
    }
  },
  
  // Convenience methods for common client patterns
  auth: (message: string, meta?: any) => {
    // Authentication logs are sensitive - minimal in production
    if (process.env.NODE_ENV === 'production') {
      logger.info('[AUTH] User authentication event');
    } else {
      logger.debug(`[AUTH] ${message}`, meta);
    }
  },
  
  api: (message: string, meta?: any) => {
    // API logs - minimal in production
    if (process.env.NODE_ENV === 'production') {
      // Silent in production to avoid logging API responses
    } else {
      logger.debug(`[API] ${message}`, meta);
    }
  },
  
  ui: (message: string, meta?: any) => {
    // UI interaction logs - development only
    if (process.env.NODE_ENV === 'development') {
      logger.debug(`[UI] ${message}`, meta);
    }
  },
  
  // Performance logging
  perf: (message: string, meta?: any) => {
    if (process.env.NODE_ENV === 'development') {
      logger.debug(`[PERF] ${message}`, meta);
    }
  }
};

export default logger;

// 🔒 Production Build Info
if (process.env.NODE_ENV === 'production') {
  console.info('🛡️ Client logging: Production mode (errors only)');
} else {
  console.info(`📘 Client logging: ${process.env.NODE_ENV} mode (full logging with filtering)`);
} 